import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import CommonJs from '@/utils/commonJs';
import { CompressOutlined, DownloadOutlined, ExpandOutlined } from '@ant-design/icons';
import {
  appendStaticFileAuthParams,
  decodeFileNameSegment,
  extractFileNameFromUrl,
  isExternalLinkAttachment,
} from './fileAttachmentUtils';

export { extractFileNameFromUrl } from './fileAttachmentUtils';

const OFFICE_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.odt', '.ods', '.odp'];
const PDF_EXTENSIONS = ['.pdf'];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.m4v'];
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.flac', '.aac', '.m4a'];
const ARCHIVE_EXTENSIONS = ['.zip', '.rar'];

function getFileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return idx >= 0 ? fileName.slice(idx).toLowerCase() : '';
}

function isOfficeFile(fileName: string): boolean {
  return OFFICE_EXTENSIONS.includes(getFileExtension(fileName));
}

function isArchiveFile(fileName: string): boolean {
  return ARCHIVE_EXTENSIONS.includes(getFileExtension(fileName));
}

function isPdfFile(fileName: string): boolean {
  return PDF_EXTENSIONS.includes(getFileExtension(fileName));
}

function isVideoFile(fileName: string): boolean {
  return VIDEO_EXTENSIONS.includes(getFileExtension(fileName));
}

function isAudioFile(fileName: string): boolean {
  return AUDIO_EXTENSIONS.includes(getFileExtension(fileName));
}

function isImageFile(fileName: string): boolean {
  return CommonJs.isImageFile(fileName);
}

function isInlineMediaUrl(filePath: string): boolean {
  return filePath.startsWith('blob:') || filePath.startsWith('data:');
}

function toAbsoluteUrl(filePath: string): string {
  if (!filePath) return '';
  if (isInlineMediaUrl(filePath)) return filePath;
  if (
    filePath.startsWith('http://')
    || filePath.startsWith('https://')
  ) {
    return appendStaticFileAuthParams(filePath);
  }
  const cleanPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
  return appendStaticFileAuthParams(`${window.location.origin}${cleanPath}`);
}

function toOfficeFetchableUrl(fileUrl: string): string {
  // Với endpoint /file/static cần query download để Office viewer nhận đúng binary stream.
  return CommonJs.makeLinkDownload(fileUrl);
}

function isPublicHttpUrl(urlString: string): boolean {
  try {
    const u = new URL(urlString);
    if (!/^https?:$/.test(u.protocol)) return false;
    const h = u.hostname.toLowerCase();
    if (h === 'localhost' || h === '127.0.0.1' || h === '::1') return false;
    if (h.startsWith('10.') || h.startsWith('192.168.')) return false;
    if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(h)) return false;
    return true;
  } catch {
    return false;
  }
}

export interface FilePreviewModalProps {
  open: boolean;
  filePath: string;
  fileName?: string;
  onClose: () => void;
  zIndex?: number;
}

const IMAGE_ZOOM_MIN = 0.5;
const IMAGE_ZOOM_MAX = 5;
const IMAGE_ZOOM_STEP = 0.1;

interface ImageZoomPreviewProps {
  src: string;
  alt: string;
  maxHeight: string;
}

const ImageZoomPreview: React.FC<ImageZoomPreviewProps> = ({ src, alt, maxHeight }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef({ startX: 0, startY: 0, originX: 0, originY: 0 });

  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [src]);

  useEffect(() => {
    if (scale <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [scale]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const direction = event.deltaY > 0 ? -1 : 1;
      setScale((prev) => {
        const next = prev + direction * IMAGE_ZOOM_STEP;
        return Math.min(IMAGE_ZOOM_MAX, Math.max(IMAGE_ZOOM_MIN, Math.round(next * 100) / 100));
      });
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event: MouseEvent) => {
      const { startX, startY, originX, originY } = dragStateRef.current;
      setPosition({
        x: originX + (event.clientX - startX),
        y: originY + (event.clientY - startY),
      });
    };

    const stopDragging = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopDragging);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopDragging);
    };
  }, [isDragging]);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (scale <= 1 || event.button !== 0) return;

    event.preventDefault();
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
    setIsDragging(true);
  };

  const canDrag = scale > 1;

  return (
    <div
      ref={containerRef}
      className={`file-preview-image-zoom${canDrag ? ' file-preview-image-zoom--draggable' : ''}${isDragging ? ' file-preview-image-zoom--dragging' : ''}`}
      style={{ maxHeight }}
      title={canDrag ? 'Cuộn chuột để zoom, kéo để di chuyển ảnh' : 'Cuộn chuột để phóng to / thu nhỏ'}
      onMouseDown={handleMouseDown}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          maxWidth: '100%',
          maxHeight,
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transformOrigin: 'center center',
          transition: isDragging ? 'none' : 'transform 0.08s ease-out',
        }}
      />
      {scale !== 1 && (
        <span className="file-preview-image-zoom__badge">{Math.round(scale * 100)}%</span>
      )}
    </div>
  );
};

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  open,
  filePath,
  fileName,
  onClose,
  zIndex = 1300,
}) => {
  const resolvedName = decodeFileNameSegment(fileName || extractFileNameFromUrl(filePath));
  const resolvedUrl = toAbsoluteUrl(filePath);
  const canOfficeEmbed = isPublicHttpUrl(resolvedUrl);
  const isImagePreview = isImageFile(resolvedName);
  const isPdfPreview = isPdfFile(resolvedName);
  const isArchivePreview = isArchiveFile(resolvedName);
  const originalImageUrl = isImagePreview
    ? isInlineMediaUrl(resolvedUrl)
      ? resolvedUrl
      : resolvedUrl.indexOf('?') === -1
        ? `${resolvedUrl}?view=origin`
        : `${resolvedUrl}&view=origin`
    : '';
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!open || isExternalLinkAttachment(filePath, fileName)) {
      setIsFullscreen(false);
    }
  }, [open, filePath, fileName]);

  const isLinkView = isExternalLinkAttachment(filePath, fileName);
  const isArchiveView = isArchiveFile(resolvedName);

  useEffect(() => {
    if (!open || (!isLinkView && !isArchiveView) || !resolvedUrl) return;
    window.open(CommonJs.makeLinkDownload(resolvedUrl), '_blank', 'noopener,noreferrer');
    onClose();
  }, [open, isLinkView, isArchiveView, resolvedUrl, onClose]);

  if ((isLinkView || isArchiveView) && open) return null;

  const previewHeight = isFullscreen ? 'calc(100vh - 112px)' : 'calc(90vh - 112px)';

  const getPreviewContent = () => {
    if (!resolvedUrl || !resolvedName) return null;

    if (isOfficeFile(resolvedName)) {
      if (!canOfficeEmbed) {
        return (
          <div style={{ padding: '24px 0', textAlign: 'center' }}>
            <div style={{ marginBottom: 12 }}>
              File Office này không phải URL public nên Microsoft Viewer không thể nhúng trực tiếp.
            </div>
            <Button onClick={() => window.open(resolvedUrl, '_blank')}>Mở bằng tab mới</Button>
          </div>
        );
      }

      const officeSourceUrl = toOfficeFetchableUrl(resolvedUrl);
      const encodedUrl = encodeURIComponent(officeSourceUrl);

      console.log('resolvedUrl', resolvedUrl);
      console.log('encodedUrl', encodedUrl);
      console.log('officeSourceUrl', officeSourceUrl);
      return (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`}
          style={{ width: '100%', height: previewHeight, border: 'none' }}
          title={resolvedName}
          allowFullScreen
        />
      );
    }

    if (isPdfFile(resolvedName)) {
      return (
        <iframe
          src={resolvedUrl}
          style={{ width: '100%', height: previewHeight, border: 'none' }}
          title={resolvedName}
        />
      );
    }

    if (isVideoFile(resolvedName)) {
      return (
        <video controls style={{ width: '100%', maxHeight: previewHeight }}>
          <source src={resolvedUrl} />
        </video>
      );
    }

    if (isAudioFile(resolvedName)) {
      return (
        <div className="flex items-center justify-center py-12">
          <audio controls src={resolvedUrl} />
        </div>
      );
    }

    if (isArchiveFile(resolvedName)) {
      return (
        <div style={{ padding: '24px 0', textAlign: 'center' }}>
          <div style={{ marginBottom: 12 }}>
            Định dạng nén không thể xem trực tiếp. Vui lòng tải xuống.
          </div>
          <Button
            type="primary"
            href={CommonJs.makeLinkDownload(resolvedUrl)}
            target="_blank"
            rel="noopener noreferrer"
            download
          >
            Tải xuống
          </Button>
        </div>
      );
    }

    if (isImagePreview) {
      return (
        <div className="text-center">
          <ImageZoomPreview src={resolvedUrl} alt={resolvedName} maxHeight={previewHeight} />
        </div>
      );
    }

    return null;
  };

  const previewContent = getPreviewContent();

  return (
    <Modal
      className={`file-preview-modal${isFullscreen ? ' file-preview-modal--fullscreen' : ''}`}
      open={open}
      onCancel={onClose}
      width={isFullscreen ? '100vw' : '90vw'}
      title={resolvedName || 'Xem tài liệu'}
      centered={!isFullscreen}
      style={
        isFullscreen
          ? {
              top: 0,
              paddingBottom: 0,
              maxWidth: '100vw',
              margin: 0,
            }
          : {
              maxWidth: '90vw',
            }
      }
      styles={
        isFullscreen
          ? {
              body: {
                height: 'calc(100vh - 112px)',
                overflow: 'auto',
                padding: '12px 16px',
              },
              content: {
                height: '100vh',
                maxHeight: '100vh',
                borderRadius: 0,
              },
              header: {
                borderRadius: 0,
              },
            }
          : {
              body: {
                height: 'calc(90vh - 112px)',
                overflow: 'auto',
                padding: '12px 16px',
              },
              content: {
                height: '90vh',
                maxHeight: '90vh',
              },
            }
      }
      footer={[
        ...(!isLinkView
          ? isImagePreview
            ? [
                <Button
                  key="view-origin"
                  type="primary"
                  href={originalImageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Xem hình gốc
                </Button>,
              ]
            : isPdfPreview
              ? [
                  <Button
                    key="open-link"
                    type="default"
                    onClick={() => window.open(resolvedUrl, '_blank', 'noopener,noreferrer')}
                  >
                    Mở link
                  </Button>,
                  <Button
                    key="download"
                    href={CommonJs.makeLinkDownload(resolvedUrl)}
                    target="_blank"
                    download
                    type="primary"
                    icon={<DownloadOutlined />}
                  >
                    Tải xuống
                  </Button>,
                ]
              : isArchivePreview
                ? [
                    <Button
                      key="download"
                      href={CommonJs.makeLinkDownload(resolvedUrl)}
                      target="_blank"
                      download
                      type="primary"
                      icon={<DownloadOutlined />}
                    >
                      Tải xuống
                    </Button>,
                  ]
                : [
                    <Button
                      key="fullscreen"
                      icon={isFullscreen ? <CompressOutlined /> : <ExpandOutlined />}
                      onClick={() => setIsFullscreen((prev) => !prev)}
                    >
                      {isFullscreen ? 'Thu nhỏ' : 'Toàn màn hình'}
                    </Button>,
                    <Button
                      key="download"
                      href={CommonJs.makeLinkDownload(resolvedUrl)}
                      target="_blank"
                      download
                      type="primary"
                      icon={<DownloadOutlined />}
                    >
                      Tải xuống
                    </Button>,
                  ]
          : []),
        <Button key="close" onClick={onClose}>
          Đóng
        </Button>,
      ]}
      destroyOnClose
      zIndex={zIndex}
    >
      {previewContent || (
        <div style={{ padding: '24px 0', textAlign: 'center' }}>
          <div style={{ marginBottom: 12 }}>Không hỗ trợ xem trước định dạng này.</div>
          <Button onClick={() => resolvedUrl && window.open(resolvedUrl, '_blank')}>Mở bằng tab mới</Button>
        </div>
      )}
    </Modal>
  );
};
